import { TESTDATA_MODAL } from '@enums/overlay-internal-events.enum';
import { useInternalEvents, useTranslation } from '@testsigma/atoms';
import { useCallback, useEffect, useState, useContext, useMemo } from 'react';
import DataGeneratorService from 'services/data-generator.service';
import KibbutzService from 'services/kibbutz.service';
import {
  TestDataDrawerHelpInfoProps,
  TestDataDrawerLayout,
} from './test-data-drawer-layout';
import { DataGenerator } from '@testsigmainc/models';
import { DataGeneratorValueSelector } from '@testsigma/organism';
import { useTestData } from '@overlay/hooks/test-data.hooks';
import { VersionContext } from '@modules/test-development/contexts/version.context';
import { orderBy } from 'es-toolkit/array';
import { cloneDeep } from 'es-toolkit/object';
import { useQuery } from 'react-query';
import { useReactQueryCaching } from '@hooks/use-react-query-caching';
import { CachingPrefix } from '@enums/caching-prefix.enum';
import {
  DataGeneratorDrawerProps,
  fetchMailBoxes,
  fetchPhoneNumbers,
  useTDPFetching,
} from './data-generator-drawer-utils';
import { getSymbol } from '@enums/test-data-symbols.enum';

export const DataGeneratorDrawer = (props: DataGeneratorDrawerProps) => {
  const {
    testData,
    tdpIds,
    refetchTestCase,
    getTdpIds,
    testCaseId,
    isInsideRestApi = false,
  } = props;
  const { t } = useTranslation();
  const { fetchTDPData, fetchTDPSets, openTestDataProfile } = useTDPFetching();
  const versionContext = useContext(VersionContext);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [isFunctionDataLoading, setIsFunctionDataLoading] =
    useState<boolean>(false);
  const [isSystemFunction, updateIsSystemFunction] = useState(
    !testData?.isKibbutzFn,
  );
  const [selectedFunction, updateSelectedFunction] = useState(
    new DataGenerator(),
  );
  const [selectedFileId, updateSelectedFileId] = useState<number>(0);
  const publishInternalEvents = useInternalEvents([]);
  const { getCachingTime } = useReactQueryCaching(
    CachingPrefix.TEST_DATA_DATA_GENERATOR_FUNCTION_NAME_CACHING_KEY,
    false,
  );
  const [dataGenerators, setDataGenerators] = useState();

  const {
    data: dataGeneratorFunctionNames,
    isFetching: isDataGeneratorFunctionNamesFetching,
  } = useQuery(
    CachingPrefix.TEST_DATA_DATA_GENERATOR_FUNCTION_NAME_CACHING_KEY,
    async () => await DataGeneratorService.findAllFunctionNames(),
    {
      staleTime: getCachingTime(Infinity),
    },
  );

  const { data: _dataGenerators, isFetching: isDataGeneratorsFetching } =
    useQuery(
      CachingPrefix.TEST_DATA_DATA_GENERATOR_FUNCTION_CACHING_KEY +
        selectedFileId,
      async () => await DataGeneratorService.findAllByFileId(selectedFileId),
      {
        staleTime: getCachingTime(Infinity),
        enabled: !!selectedFileId && isSystemFunction,
      },
    );

  useEffect(() => {
    setDataGenerators(cloneDeep(_dataGenerators));
  }, [_dataGenerators]);

  const { data: kibbutzFunctionName, isFetching: isKibbutzFunctionFetching } =
    useQuery(
      CachingPrefix.TEST_DATA_DATA_GENERATOR_KIBBUTZ_FUNCTION_NAME_CACHING_KEY,
      async () => {
        const res = await KibbutzService.findAllFunctions(
          `?query=deprecated:false`,
        );
        let processedData = {};
        if (res?.content?.length)
          processedData = getKibbutzFunctionNames(res?.content);
        return processedData;
      },
      {
        staleTime: getCachingTime(Infinity),
      },
    );

  const isLoading = useMemo(() => {
    return (
      isDataGeneratorFunctionNamesFetching ||
      isDataGeneratorsFetching ||
      isKibbutzFunctionFetching ||
      isFunctionDataLoading
    );
  }, [
    isDataGeneratorFunctionNamesFetching,
    isDataGeneratorsFetching,
    isKibbutzFunctionFetching,
    isFunctionDataLoading,
  ]);

  //  Data generator drawer close handler
  const onClose = useCallback(
    () =>
      publishInternalEvents(
        TESTDATA_MODAL.CLOSE_DATA_GENERATOR,
        isInsideRestApi ? { isInsideRestApi: true } : null,
      ),
    [publishInternalEvents],
  );

  // Data generator drawer submit handler with data
  const onDataClick = useCallback(
    (item) => {
      publishInternalEvents(TESTDATA_MODAL.SELECTED_DATA_GENERATOR, item);
    },
    [publishInternalEvents],
  );

  // Data generator drawer submit handler
  const onReturnValue = useCallback(
    (val) => {
      if (val) {
        onDataClick(val);
      } else {
        onClose();
      }
    },
    [onDataClick, onClose],
  );

  const helpInfoProps: TestDataDrawerHelpInfoProps = {
    description: t('modal.help.data_generator'),
    url: 'https://testsigma.com/docs/test-data/types/data-generator/',
  };

  const getTestDataHandler = useTestData('DATA_GENERATOR');
  const getTestData = async (props) => {
    return await getTestDataHandler({
      ...props,
      appVersionId: versionContext?.version?.id,
      getTdpIds,
      projectId: versionContext?.version?.application?.projectId,
      tdpIds,
      profileList: props?.profileList,
      refetchTestCase,
      testCaseId,
      applicationId: versionContext?.version?.applicationId,
    });
  };

  const getKibbutzFunctionNames = (data) => {
    const functionNamesList = {};
    data.map((item) => {
      functionNamesList[item.displayName] = item.id;
    });
    return functionNamesList;
  };

  const fetchKibbutzFunction = (functionId, isCleared = false) => {
    if (functionId) {
      const testDataArgument = testData?.testDataKibbutzFunction?.arguments
        ? testData?.testDataKibbutzFunction?.arguments
        : [];
      updateSelectedFileId(functionId);
      setIsFunctionDataLoading(true);
      KibbutzService.showFunction(functionId)
        .then((res) => {
          const totalResponse = {
            ...res,
            parameters: isCleared
              ? res?.parameters
              : res?.parameters?.map((arg) => {
                  if (testDataArgument.hasOwnProperty(arg.name)) {
                    // Update the value of arg if a match is found
                    arg.value = testDataArgument[arg.name];
                  }
                  return arg;
                }),
          };
          updateSelectedFunction(totalResponse);
        })
        .catch((err) => {
          console.log(err);
          updateSelectedFunction({});
        })
        .finally(() => setIsFunctionDataLoading(false));
    }
  };

  // Default related Functions.

  const fetchDefaultFunctions = (functionId) => {
    const _arguments =
      testData?.testDataFunction?.arguments || testData?.arguments;
    // If arg0  ,arg 2 , arg1 ,... arg10 comes
    // then result need to be arg0 , arg1 , arg2 ,... arg10
    // So converting it into number and sorting
    const sortedTestDataArgument = _arguments
      ? orderBy(
          _arguments,
          [(item) => Number(item?.argumentName?.match(/\d+/)?.[0]) || 0],
          ['asc'],
        )
      : [];
    setIsFunctionDataLoading(true);
    DataGeneratorService.show(functionId)
      .then((res: any) => {
        const totalResponse = {
          ...res,
          arguments: res?.arguments.length
            ? res?.arguments?.map((arg) => {
                const match = sortedTestDataArgument.find(
                  (d) => d.argumentName === arg.argumentName,
                );
                if (match) {
                  arg.argumentValue = match.argumentValue;
                }
                return arg;
              })
            : res.id === functionId
            ? sortedTestDataArgument
            : [],
        };
        updateSelectedFileId(res.fileId);
        updateSelectedFunction(totalResponse);
      })
      .finally(() => setIsFunctionDataLoading(false));
  };

  const getInitialData = () => {
    if (!testData) {
      // fetching first functionType's function

      updateIsSystemFunction(true);
      updateSelectedFileId(Object.values(dataGeneratorFunctionNames)?.[0]);
      updateSelectedFunction(
        new DataGenerator().deserialize(dataGenerators?.[0]),
      );
    } else {
      // fetching already selected functionType's function
      // Checking isKibbutz on below conditions.
      // 1. "isKibbutzFn" for normal test data,
      // 2. "dataGeneratorId" check for overriden kibbutz data-generator test data
      if (!testData?.isKibbutzFn && !testData?.dataGeneratorId) {
        updateIsSystemFunction(true);
        if (!selectedFileId) {
          fetchDefaultFunctions(testData?.testDataFunctionId);
        }
      } else {
        updateIsSystemFunction(false);
        fetchKibbutzFunction(
          testData?.testDataKibbutzFunctionId || testData?.dataGeneratorId,
        );
      }
    }
  };

  useEffect(() => {
    if (dataGeneratorFunctionNames && !isDirty) {
      getInitialData();
    }
  }, [dataGeneratorFunctionNames, dataGenerators]);

  useEffect(() => {
    // Updation of default selected function as first one in the list
    if (_dataGenerators && isDirty && isSystemFunction) {
      updateSelectedFunction(
        new DataGenerator().deserialize(_dataGenerators?.[0]),
      );
    }
  }, [_dataGenerators]);

  const onChangeType = (isDefaultType) => {
    setIsDirty(true);
    updateIsSystemFunction(isDefaultType);
    if (!isDefaultType) {
      fetchKibbutzFunction(Object.values(kibbutzFunctionName)?.[0], true);
    } else {
      updateSelectedFileId(Object.values(dataGeneratorFunctionNames)?.[0]);
    }
  };

  const onChangeFunctionName = (fileId) => {
    setIsDirty(true);
    if (!isSystemFunction) {
      fetchKibbutzFunction(fileId, true);
    }
    updateSelectedFileId(fileId);
  };

  const onChangeFunction = (data) => {
    setIsDirty(true);
    updateSelectedFunction(data);
  };

  const functionNames = useMemo(() => {
    return isSystemFunction
      ? dataGeneratorFunctionNames
      : kibbutzFunctionName || [];
  }, [isSystemFunction, dataGeneratorFunctionNames, kibbutzFunctionName]);

  return (
    <TestDataDrawerLayout
      title={t('modal.title.data.generator')}
      symbol={getSymbol('DATA_GENERATOR')}
      onClose={onClose}
      helpInfo={helpInfoProps}
      className='!h-full'
    >
      <DataGeneratorValueSelector
        dataGenerators={dataGenerators || []}
        onReturnValue={onReturnValue}
        onChangeType={onChangeType}
        onChangeFunctionName={onChangeFunctionName}
        onChangeFunction={onChangeFunction}
        functionNames={functionNames || []}
        selectedFunctionType={selectedFileId}
        selectedFunction={selectedFunction}
        selectedDataGeneratorType={isSystemFunction}
        fetchPhoneNumbers={fetchPhoneNumbers}
        fetchMailBoxes={fetchMailBoxes}
        getTestData={getTestData}
        isLoading={isLoading}
        fetchTDPData={fetchTDPData}
        fetchTDPSets={fetchTDPSets}
        openTestDataProfile={openTestDataProfile}
        isInsideRestApi={isInsideRestApi}
      />
    </TestDataDrawerLayout>
  );
};
